import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, uploadFile, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MessagesSquare, Plus, MessageCircle, ImagePlus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

export default function Forum() {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [topics, setTopics] = useState([]);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ category: "identification", title: "", body: "", image_url: "" });

  const load = async () => {
    const [c, t] = await Promise.all([api.get("/forum/categories"), api.get("/forum/topics")]);
    setCategories(c.data);
    setTopics(t.data);
  };
  useEffect(() => { load(); }, []);

  const attachImage = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setForm((f) => ({ ...f, image_url: url }));
      toast.success("Image attached");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const createTopic = async () => {
    if (!form.title.trim()) return toast.error("Add a title");
    try {
      await api.post("/forum/topics", form);
      toast.success("Topic posted");
      setOpen(false);
      setForm({ category: "identification", title: "", body: "", image_url: "" });
      load();
    } catch (e) {
      toast.error("Could not post");
    }
  };

  const catName = (slug) => categories.find((c) => c.slug === slug)?.name || slug;

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-8 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="font-tech text-xs uppercase tracking-widest brand-text">Community</p>
          <h1 className="font-display text-4xl font-bold">Collector Forum</h1>
        </div>
        {user ? (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="forum-new-topic-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold" style={{ background: "var(--brand)", color: "#fff" }}><Plus className="w-4 h-4" /> New topic</button>
            </DialogTrigger>
            <DialogContent className="surface">
              <DialogHeader><DialogTitle className="font-display text-2xl">Start a topic</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <select data-testid="topic-category-select" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className="w-full surface border rounded-lg px-3 py-2.5">
                  {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
                <input data-testid="topic-title-input" placeholder="Title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="w-full surface border rounded-lg px-3 py-2.5" />
                <textarea data-testid="topic-body-input" placeholder="Share the details…" rows={5} value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} className="w-full surface border rounded-lg px-3 py-2.5" />
                {form.image_url ? (
                  <div className="relative inline-block">
                    <img src={mediaUrl(form.image_url)} alt="attachment" className="max-h-48 rounded-lg border" style={{ borderColor: "var(--page-border)" }} />
                    <button type="button" data-testid="remove-topic-image-btn" onClick={() => setForm((f) => ({ ...f, image_url: "" }))} className="absolute -top-2 -right-2 w-6 h-6 rounded-full grid place-items-center text-white" style={{ background: "var(--brand)" }}><X className="w-3.5 h-3.5" /></button>
                  </div>
                ) : (
                  <label className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold surface border cursor-pointer w-fit" data-testid="topic-image-input">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} Attach a photo
                    <input type="file" accept="image/*" className="hidden" onChange={attachImage} disabled={uploading} />
                  </label>
                )}
              </div>
              <DialogFooter><button data-testid="submit-topic-btn" onClick={createTopic} className="px-5 py-2.5 rounded-full font-semibold w-full" style={{ background: "var(--brand)", color: "#fff" }}>Post topic</button></DialogFooter>
            </DialogContent>
          </Dialog>
        ) : <Link to="/login" className="px-5 py-2.5 rounded-full font-semibold surface border">Sign in to post</Link>}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-10 stagger">
        {categories.map((c) => (
          <div key={c.slug} className="surface border rounded-2xl p-5 glass-glow card-lift" data-testid={`forum-category-${c.slug}`}>
            <div className="flex items-center gap-2 mb-2"><MessagesSquare className="w-5 h-5 brand-text" /><span className="font-tech text-xs px-2 py-0.5 rounded-full" style={{ background: "var(--honey-100)", color: "var(--honey-900)" }}>{c.topic_count} topics</span></div>
            <h3 className="font-display text-xl font-bold">{c.name}</h3>
            <p className="text-muted-page text-sm mt-1">{c.desc}</p>
          </div>
        ))}
      </div>

      <h2 className="font-display text-2xl font-bold mb-4">Recent Discussions</h2>
      {topics.length === 0 ? (
        <p className="text-muted-page py-10 text-center surface border rounded-2xl">No topics yet — be the first to post!</p>
      ) : (
        <div className="surface border rounded-2xl overflow-hidden" data-testid="forum-topic-list">
          {topics.map((t) => (
            <Link key={t.id} to={`/forum/${t.id}`} className="flex items-center justify-between px-5 py-4 border-b hover:bg-black/[0.02]" style={{ borderColor: "var(--page-border)" }} data-testid={`forum-topic-${t.id}`}>
              <div>
                <div className="font-display text-lg font-semibold">{t.title}</div>
                <div className="text-xs text-muted-page">{catName(t.category)} · by {t.author_name}</div>
              </div>
              <div className="flex items-center gap-1 text-muted-page text-sm font-tech"><MessageCircle className="w-4 h-4" /> {t.reply_count}</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
